import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  Injector,
  signal,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { SidebarComponent } from '../../../sidebar.component';
import { SidebarFlatTasksListComponent } from '../../sidebar-flat-tasks-list/sidebar-flat-tasks-list.component';
import { LazyLoadContainerComponent } from '../../lazy-load-container/lazy-load-container.component';
import { LimitedResultOfTaskGroupDto } from '@r1-development/resolution-mechanicalturk-api-client';
import { SidebarApiService } from '../../../services/sidebar-api.service';
import {
  GetTaskGroupsParams,
  TaskTypes,
} from 'src/app/shared/models/tasks.model';
import {
  SidebarListsContract,
  SidebarTaskGroupUi,
  SidebarTaskUi,
} from '../../../model/sidebar.model';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'ri-resolution-ui-completed-tasks',
  imports: [
    SidebarFlatTasksListComponent,
    LazyLoadContainerComponent,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatIcon,
  ],
  templateUrl: './completed-tasks.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompletedTasksComponent implements SidebarListsContract<GetTaskGroupsParams> {
  private readonly sidebarApiService = inject(SidebarApiService);
  private readonly injector = inject(Injector);

  private readonly sidebarComponentComponent = inject<SidebarComponent>(
    forwardRef(() => SidebarComponent)
  );

  readonly form = this.sidebarComponentComponent.form;

  public readonly allowedTaskTypes =
    this.sidebarComponentComponent.allowedTaskTypes;

  private taskGroupsListGenerator?: AsyncGenerator<LimitedResultOfTaskGroupDto>;

  private readonly _groups = signal<SidebarTaskGroupUi[]>([]);
  private readonly groups = this._groups.asReadonly();

  private readonly _hasGroupsToFetch = signal<boolean>(false);
  public readonly hasGroupsToFetch = this._hasGroupsToFetch.asReadonly();

  private readonly _isLoading = signal<boolean>(false);
  public readonly isLoading = this._isLoading.asReadonly();
  public readonly reloadParams = signal<GetTaskGroupsParams | null>(null);

  public readonly flatTasksList = computed<SidebarTaskUi[]>(() => {
    const groups = this.groups();
    const user = this.sidebarApiService.user();
    return this.filterToUserTasks(user?.sub || null, groups);
  });

  public readonly hasTasksLessThanLimit = computed<boolean>(() => {
    const groups = this.groups();
    return (
      groups.length > 0 && groups.length < SidebarApiService.LOAD_PAGE_SIZE
    );
  });

  public readonly hasNoTasks = computed<boolean>(() => {
    const groups = this.groups();
    return groups.length === 0;
  });

  public readonly taskTypes = TaskTypes;

  constructor() {
    effect(() => {
      const params = this.reloadParams();
      if (!params) {
        return;
      }
      const user = this.sidebarApiService.user();
      if (!user) {
        return;
      }
      params.completedBy = user.sub;
      this.taskGroupsListGenerator =
        this.sidebarApiService.createCompletedTasksListGenerator(params);

      if (params.taskTypes && params.taskTypes.length > 0) {
        this.loadTaskGroups({ reload: true });
      }
    });
  }

  reloadData(params: GetTaskGroupsParams): void {
    this._groups.set([]);
    this._hasGroupsToFetch.set(false);
    this.reloadParams.set(params);
  }

  async loadTaskGroups(
    options: { reload: boolean } = { reload: false }
  ): Promise<void> {
    this._isLoading.set(true);

    try {
      const result = await SidebarComponent.loadTaskGroups(
        this.taskGroupsListGenerator!
      );

      if (result.done) {
        return;
      }

      if (options.reload) {
        this._groups.set(result.groups);
      } else {
        this._groups.update((oldGroups) => [...oldGroups, ...result.groups]);
      }

      this._hasGroupsToFetch.set(result.hasMore);
    } finally {
      this._isLoading.set(false);
    }
  }

  filterToUserTasks(userId: string | null, groups: SidebarTaskGroupUi[]) {
    if (groups.length === 0) {
      return [];
    }

    const tasks: SidebarTaskUi[] = [];
    groups.forEach((group) => {
      if (group.completedById === userId) {
        group.tasks.forEach((task) => tasks.push(task));
      } else {
        group.tasks.forEach((task) => {
          if (task.completedById === userId) {
            tasks.push(task);
          }
        });
      }
    });
    return tasks;
  }
}
